import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import { listAudit } from '@/api/audit'
import { formatMoscowDateTime } from '@/utils/format'
import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { SearchInput } from '@/components/ui/SearchInput'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import type { AuditLogRow } from '@/types/audit'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

function resolveUserName(r: AuditLogRow): string {
  if (r.user_name && r.user_name.length > 0) return r.user_name
  if (USE_MOCK) {
    const p = mockState.profiles.find((p) => p.id === r.user_id)
    if (p?.full_name) return p.full_name
  }
  return r.user_id || '—'
}

export function AuditPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['audit'],
    queryFn: () => listAudit({ page: 1, pageSize: 50 }),
  })

  const allRows = data?.items ?? []
  const q = debouncedSearch.toLowerCase()
  const rows = q
    ? allRows.filter(
        (r) =>
          resolveUserName(r).toLowerCase().includes(q) ||
          r.action.toLowerCase().includes(q) ||
          r.object_type.toLowerCase().includes(q) ||
          r.object_id.toLowerCase().includes(q),
      )
    : allRows

  return (
    <div>
      <PageHeader
        title={t('logs.audit.title')}
        filters={
          <SearchInput
            placeholder={t('logs.audit.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
      />
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[color:var(--stryzh-color-status-error)] bg-[color:var(--stryzh-color-status-error-bg,transparent)] px-3 py-2 text-sm text-[color:var(--stryzh-color-status-error)]"
        >
          {t('logs.audit.loadError', { message: (error as Error).message })}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2">{t('logs.audit.colDate')}</th>
              <th className="px-3 py-2">{t('logs.audit.colUser')}</th>
              <th className="px-3 py-2">{t('logs.audit.colAction')}</th>
              <th className="px-3 py-2">{t('logs.audit.colObject')}</th>
              <th className="px-3 py-2">{t('logs.audit.colDetails')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className={emptyTableCellClassName}>
                  <Loader size="sm" centered label={t('common.loading')} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--stryzh-color-border-default)]">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatMoscowDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2">{resolveUserName(r)}</td>
                  <td className="px-3 py-2">
                    <span className="rounded border border-[var(--stryzh-color-border-default)] px-2 py-0.5 text-xs">
                      {r.action}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.object_type === 'orders' || r.object_type === 'order' ? (
                      <Link
                        className="text-[var(--stryzh-color-accent-orange)] hover:underline"
                        to={`/orders/${r.object_id}`}
                      >
                        {r.object_type}:{r.object_id.slice(0, 8)}…
                      </Link>
                    ) : (
                      `${r.object_type}:${r.object_id}`
                    )}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 font-mono text-xs">
                    {JSON.stringify(r.details)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {!isLoading && isFetching ? (
          <div className="border-t border-[var(--stryzh-color-border-default)] px-3 py-2 text-xs text-[var(--stryzh-color-text-secondary)]">
            {t('logs.audit.refreshing')}
          </div>
        ) : null}
      </div>
    </div>
  )
}
